/**
 * The seam for callers that are not React.
 *
 * Exactly one exists: the Froala toolbar button in
 * `web/app/common/froalaCustomInternalLink.js`. Froala plugins are registered globally
 * against the `FroalaEditor` object, so that file runs outside React and outside Angular
 * and cannot use a hook or a context.
 *
 * Before SURF-4809 it solved that by reaching into the Angular injector
 * (`angular.element(document.body).injector().get('internalLinkService')`). This is the
 * same idea with the dependency pointed the other way: the React provider registers the
 * opener on mount, and Froala asks for it when the button is clicked.
 *
 * Why a module-level variable and not a context: the registration has to survive being read
 * from a callback that React knows nothing about, at a time React is not rendering.
 */

let showLinkPickerImpl = null;

/**
 * Called by `CmsReactContext` on mount. Returns the unregister function, so the provider
 * can clean up on unmount and a remount cannot leave a stale closure behind.
 */
export function registerLinkPicker(fn) {
  showLinkPickerImpl = fn;
  return () => {
    if (showLinkPickerImpl === fn) showLinkPickerImpl = null;
  };
}

/**
 * Open the link picker from outside React.
 *
 * Resolves to `{ returnVal, targetLabel, sso }`, or `null` if the dialog was dismissed.
 *
 * Resolves to `null` rather than throwing when nothing is registered. That case means the
 * React root has not mounted yet, and the Froala button is reachable from a toolbar that
 * can in principle render first; a dismissed-looking result leaves the editor untouched,
 * which is the safe reading.
 */
export function showLinkPicker(args) {
  if (!showLinkPickerImpl) return Promise.resolve(null);
  return showLinkPickerImpl(args);
}
